import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Callout,
  Card,
  Flex,
  Heading,
  SegmentedControl,
  Text,
} from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { getCloudflare, getUsage } from "../api/admin";
import { usd, num, tokens } from "../lib/format";

const WINDOWS = [
  { key: "7", label: "7d" },
  { key: "30", label: "30d" },
  { key: "90", label: "90d" },
];

export default function Analytics() {
  const [win, setWin] = useState("30");
  const days = Number(win);

  const usage = useQuery({
    queryKey: ["usage", days],
    queryFn: () => getUsage(days),
  });
  const cf = useQuery({
    queryKey: ["cloudflare", days],
    queryFn: () => getCloudflare(days),
  });

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="center" wrap="wrap" gap="2">
        <Heading size="5">Analytics</Heading>
        <SegmentedControl.Root value={win} onValueChange={setWin} size="1">
          {WINDOWS.map((w) => (
            <SegmentedControl.Item key={w.key} value={w.key}>
              {w.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </Flex>

      {/* Token usage by user */}
      <Card size="3">
        <Flex direction="column" gap="3">
          <Heading size="4">Token usage by user</Heading>

          {usage.data && !usage.data.configured && (
            <Callout.Root size="1" color="amber">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                Analytics Engine isn't configured yet. Set{" "}
                <code>CF_ACCOUNT_ID</code> + <code>CF_ANALYTICS_TOKEN</code> on
                the hq worker to populate this.
              </Callout.Text>
            </Callout.Root>
          )}
          {usage.data?.error && (
            <Text size="1" color="red">
              {usage.data.error}
            </Text>
          )}

          {usage.data?.configured && (
            <>
              <div className="hq-cards">
                <Mini label="Spend" value={usd(usage.data.totals.costMcents)} />
                <Mini label="Turns" value={num(usage.data.totals.turns)} />
                <Mini
                  label="Input tokens"
                  value={tokens(usage.data.totals.inputTokens)}
                />
                <Mini
                  label="Output tokens"
                  value={tokens(usage.data.totals.outputTokens)}
                />
                <Mini
                  label="Cached input"
                  value={tokens(usage.data.totals.cachedInputTokens)}
                />
              </div>

              <table className="hq-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th className="num">Turns</th>
                    <th className="num">In</th>
                    <th className="num">Out</th>
                    <th className="num">Cached</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.data.rows.map((r) => (
                    <tr key={r.userId}>
                      <td>{r.email ?? r.userId}</td>
                      <td className="num">{num(r.turns)}</td>
                      <td className="num">{tokens(r.inputTokens)}</td>
                      <td className="num">{tokens(r.outputTokens)}</td>
                      <td className="num">{tokens(r.cachedInputTokens)}</td>
                      <td className="num">{usd(r.costMcents)}</td>
                    </tr>
                  ))}
                  {usage.data.rows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <Text size="2" className="hq-muted">
                          No usage in this window.
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
          {usage.isLoading && <Text className="hq-muted">Loading…</Text>}
        </Flex>
      </Card>

      {/* Cloudflare platform */}
      <Card size="3">
        <Flex direction="column" gap="3">
          <Heading size="4">Cloudflare · Workers</Heading>

          {cf.data && !cf.data.configured && (
            <Callout.Root size="1" color="amber">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                Set <code>CF_ACCOUNT_ID</code> + <code>CF_ANALYTICS_TOKEN</code>{" "}
                to show Workers invocation analytics.
              </Callout.Text>
            </Callout.Root>
          )}
          {cf.data?.error && (
            <Text size="1" color="red">
              {cf.data.error}
            </Text>
          )}

          {cf.data?.configured && (
            <table className="hq-table">
              <thead>
                <tr>
                  <th>Script</th>
                  <th className="num">Requests</th>
                  <th className="num">Errors</th>
                  <th className="num">Subrequests</th>
                </tr>
              </thead>
              <tbody>
                {cf.data.workers.map((w) => (
                  <tr key={w.scriptName}>
                    <td>{w.scriptName}</td>
                    <td className="num">{num(w.requests)}</td>
                    <td className="num">
                      {w.errors > 0 ? (
                        <Badge color="red" variant="soft">
                          {num(w.errors)}
                        </Badge>
                      ) : (
                        num(w.errors)
                      )}
                    </td>
                    <td className="num">{num(w.subrequests)}</td>
                  </tr>
                ))}
                {cf.data.workers.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <Text size="2" className="hq-muted">
                        No data in this window.
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {cf.isLoading && <Text className="hq-muted">Loading…</Text>}
        </Flex>
      </Card>
    </Flex>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <Card size="1">
      <Flex direction="column" gap="1">
        <Text size="1" className="hq-muted">
          {label}
        </Text>
        <Heading size="4">{value}</Heading>
      </Flex>
    </Card>
  );
}
